/**
 * RBAC — the single source of truth. Components NEVER hardcode role strings;
 * they call usePermission()/`<Can>` which delegate here.
 *
 * FIX(perm-admin-keys-only): capability comes from the server's PERMISSION KEYS
 * and nothing else — `/me`'s flat `permissions` (platform keys) and
 * `platformSurfacePermissions` (what `requirePlatformPerm` grants). Before: role
 * NAMES were authority too (a `superadmin` slug = wildcard, `admin` =
 * requireAdmin, a built-in role-name mirror of the surface keys, a five-role
 * fallback matrix, a `workspace_admin` scope rule). After: none of that — a
 * payload without the lists grants nothing, and the server stays the authority on
 * every request.
 */

/**
 * Every gated capability in the app. Naming: `<domain>:<verb>`. Read verbs are
 * `view`; mutations are explicit. High-risk actions are also listed in
 * HIGH_RISK_ACTIONS below (they additionally require step-up auth).
 */
export type Action =
  | 'dashboard:view'
  | 'dashboard:analytics'
  | 'workspaces:view'
  | 'workspaces:create'
  | 'workspaces:edit'
  | 'workspaces:suspend'
  | 'workspaces:changePlan'
  | 'workspaces:adjustLimits'
  | 'workspaces:impersonate'
  | 'workspaces:delete'
  | 'floors:view'
  | 'floors:create'
  | 'floors:edit'
  | 'floors:delete'
  | 'users:view'
  | 'users:create'
  | 'users:edit'
  | 'users:resetPassword'
  | 'users:forceLogout'
  | 'users:disable'
  | 'users:changeRole'
  | 'users:delete'
  | 'users:export'
  | 'roles:view'
  | 'roles:manage'
  | 'projects:view'
  | 'projects:create'
  | 'projects:edit'
  | 'projects:archive'
  | 'projects:manageMembers'
  | 'rooms:view'
  | 'rooms:endSession'
  | 'rooms:eject'
  | 'rooms:lock'
  | 'meetings:view'
  | 'meetings:create'
  | 'meetings:manage'
  | 'meetings:rerun'
  | 'meetings:delete'
  | 'recordings:view'
  | 'recordings:play'
  | 'recordings:retention'
  | 'recordings:delete'
  | 'tasks:view'
  | 'tasks:create'
  | 'tasks:reassign'
  | 'tasks:edit'
  | 'tasks:transition'
  | 'tasks:comment'
  | 'tasks:delete'
  | 'messaging:view'
  | 'docs:view'
  | 'docs:manageMembers'
  | 'docs:manageShares'
  | 'docs:revokeShare'
  | 'docs:restore'
  | 'docs:purge'
  | 'integrations:view'
  | 'integrations:resync'
  | 'integrations:revoke'
  | 'providers:view'
  | 'providers:manage'
  | 'providers:rotateKey'
  | 'providers:test'
  | 'billing:view'
  | 'billing:applyCredit'
  | 'billing:changePlan'
  | 'billing:refund'
  | 'audit:view'
  | 'audit:export'
  | 'attendance:view'
  | 'attendance:export'
  | 'security:view'
  | 'security:manage'
  | 'errors:view'
  | 'errors:manage'
  | 'notifications:view'
  | 'notifications:broadcast'
  | 'files:view'
  | 'files:manage'
  | 'system:view'
  | 'system:toggleFlag'
  | 'system:retryJob'
  | 'system:incidentBanner'
  | 'system:logging'
  | 'system:rotateKeys'
  | 'releases:view'
  | 'releases:upload'
  | 'releases:update'
  | 'releases:publish'
  | 'releases:delete'
  | 'settings:view'
  | 'settings:edit'
  | 'agents:view'
  | 'agents:manage'
  | 'aiOversight:view';

/**
 * Actions that ALSO require recent step-up re-auth, regardless of role.
 *
 * FIX(admin-stepup-server-enforced): the prompt this list drives is the UX
 * half. The server enforces step-up (`x-step-up-token`, sent by the mutation
 * via `stepUpHeaders()`) on: workspaces:delete, workspaces:impersonate,
 * users:forceLogout, users:delete, docs:purge, system:incidentBanner (publish)
 * — plus users:resetPassword, users:changeRole to/from admin|superadmin and
 * ownership transfer, which prompt at their call sites. Still browser-only
 * (their routes are product/module routes, not the admin surface):
 * recordings:delete, tasks:delete, providers:rotateKey, releases:publish,
 * releases:delete. No server route exists yet for billing:refund or
 * system:rotateKeys.
 */
export const HIGH_RISK_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  'workspaces:delete',
  'workspaces:impersonate',
  'users:forceLogout',
  'users:delete',
  'recordings:delete',
  'tasks:delete',
  'billing:refund',
  'system:rotateKeys',
  'system:incidentBanner',
  'providers:rotateKey',
  'docs:purge',
  // Publishing pushes a new binary to every installed desktop app; deleting drops
  // the bytes a published manifest may still reference.
  'releases:publish',
  'releases:delete',
]);

/**
 * How the backend's REAL permissions become admin Actions. The mapping follows
 * how each server route is actually gated (server/src/db/permissions.ts
 * PLATFORM_PERMS, modules/admin/admin.surface.ts,
 * docs/modules/users/server.md#enforcement):
 *
 *  1. PLATFORM keys — resolved from the GLOBAL role on every surface, so the
 *     `/me` list reports them exactly. Trusted as-is. (`system.ops.read` — the
 *     operations dashboard / health / analytics — is one of them; so is
 *     `params.manage`, which also covers log level/stats.)
 *  2. ORG keys the ADMIN SURFACE gates via `requirePlatformPerm` (the GLOBAL
 *     role; the wildcard is universal there). `/me` resolves org keys from the
 *     active-org MEMBERSHIP instead, so they are NEVER taken from `permissions`:
 *     they come from the session's `platformSurfacePermissions` (the server's own
 *     `hasPlatformSurfacePerm` answer — a superadmin's list carries every key).
 *  3. PRODUCT org keys (tracker, meetings) — the MUTATIONS only: they still go
 *     to the product routes (`requirePerm` → active-org membership), which is
 *     exactly what the list reports, and only make sense for the active org.
 *     Granted only to someone who is staff by 1–2.
 *
 * Anyone who holds nothing from 1–2 is not platform staff and gets NO actions.
 * Domains the server does not implement (billing, integrations, job retry, key
 * rotation, admin settings edits) are backed by no key, so they are granted to
 * nobody and their nav/pages stay hidden.
 */

/** 1. Platform keys → actions (the `/me` list is authoritative for these). */
const PLATFORM_PERM_ACTIONS: Record<string, Action[]> = {
  // GET /api/admin/roles + /permissions accept users.manage OR roles.manage, so
  // users.manage also reads the Roles page (read-only without roles.manage).
  'users.manage': [
    'users:view', 'users:create', 'users:edit', 'users:resetPassword',
    'users:forceLogout', 'users:disable', 'users:changeRole', 'users:delete', 'users:export',
    'roles:view',
  ],
  'roles.manage': ['roles:view', 'roles:manage', 'users:changeRole'],
  // POST /api/admin/impersonate/:userId — requirePerm('users.impersonate') + step-up.
  'users.impersonate': ['workspaces:impersonate'],
  // Desktop app version control (desktop/DISTRIBUTION.md). One server permission
  // grants the whole surface; the UI still gates publish/delete behind step-up.
  'desktop.releases.manage': [
    'releases:view', 'releases:upload', 'releases:update', 'releases:publish', 'releases:delete',
  ],
  // audit.read is the cross-tenant compliance-read perm; it also gates the
  // server's Messaging feed (/api/admin/messaging/*), so it grants that nav too.
  'audit.read': ['audit:view', 'audit:export', 'messaging:view'],
  'security.read': ['security:view'],
  'security.manage': ['security:view', 'security:manage'],
  // Error tracking (modules/errors): triage = set a group's status.
  'errors.read': ['errors:view'],
  'errors.manage': ['errors:view', 'errors:manage'],
  // Notification delivery ledger (modules/notifications admin API): read = ledger, devices,
  // broadcasts list; broadcast = send/cancel a broadcast, retry a delivery, test/remove a device.
  'notifications.read': ['notifications:view'],
  'notifications.broadcast': ['notifications:view', 'notifications:broadcast'],
  'files.manage': ['files:view', 'files:manage'],
  // FIX(admin-perms-recordings): the Recordings configuration page reads and
  // writes `/api/admin/params` (recording/minutes/transcription categories) and
  // `/api/admin/transcription/health` — all `params.manage`. Before: gated on
  // the unbacked `recordings:view`. After: `recordings:retention`, from here.
  'params.manage': ['system:view', 'system:toggleFlag', 'system:logging', 'recordings:retention'],
  // /api/dashboard, system health, queues — global-role-only since the role-name
  // `requireAdmin` gate was replaced by this key.
  'system.ops.read': ['dashboard:view', 'dashboard:analytics', 'system:view'],
  'incidents.read': ['system:view'],
  'incidents.manage': ['system:view', 'system:incidentBanner'],
  'docs.admin': ['docs:view', 'docs:manageMembers', 'docs:manageShares', 'docs:revokeShare', 'docs:restore', 'docs:purge'],
  // FIX(admin-org-scoped-reads): the Meetings / Recordings / Tasks / Projects
  // READS go to the read-only cross-org admin endpoints
  // (`/api/admin/meetings|recordings|tracker/*`), gated on the PLATFORM key
  // `oversight.read` (global `admin` role + superadmin only). Before (first
  // cut): derived from `meetings.view`/`tracker.view` in the platform-surface
  // list — ORG keys the seeded global `member` role also holds, so a plain
  // member counted as staff and got the panel (plus the family-4 write actions).
  // After: this platform key; the WRITE actions stay in family 4 (org list),
  // because the writes stay on the org-scoped product routes.
  'oversight.read': ['meetings:view', 'recordings:view', 'recordings:play', 'tasks:view', 'projects:view', 'agents:view'],
};

/** 2. Org keys the admin surface checks against the GLOBAL role. */
const SURFACE_ORG_PERM_ACTIONS: Record<string, Action[]> = {
  // "Workspaces" is the real tenant model (/api/admin/workspaces + /orgs/*).
  'workspaces.manage': [
    'workspaces:view', 'workspaces:create', 'workspaces:edit', 'workspaces:suspend',
    'workspaces:changePlan', 'workspaces:adjustLimits', 'workspaces:delete',
    // Hire / edit / fire an org's AI employees (the server also requires `oversight.read`,
    // which is what shows the pages — `agents:view`).
    'agents:manage',
  ],
  'floors.manage': ['floors:view', 'floors:create', 'floors:edit', 'floors:delete'],
  moderate: ['rooms:view', 'rooms:endSession', 'rooms:eject', 'rooms:lock'],
  // Room visit tracking → the admin Attendance report + the user Room-history tab.
  'attendance.read': ['attendance:view', 'attendance:export'],
  // /api/ai/providers* is requirePlatformPerm('ai.manage').
  'ai.manage': ['providers:view', 'providers:manage', 'providers:rotateKey', 'providers:test'],
  // Cross-org AI oversight (`/api/admin/ai/{conversations,projects}*`) — read-only,
  // every open is audit-logged server-side. An ORG key checked against the GLOBAL
  // role on the admin surface. FIX(ai-oversight-gate): Before: `oversight.read`
  // (family 1) also granted `aiOversight:view`, so a staff role with only that
  // key saw the nav but the server's `['ai.oversight']` gate 403'd every call.
  // After: only `ai.oversight` grants it. A superadmin still reaches it —
  // the server's `hasPlatformSurfacePerm` treats `*` as universal, so its
  // `platformSurfacePermissions` list carries `ai.oversight` and its gate passes too.
  'ai.oversight': ['aiOversight:view'],
};

/**
 * 3. Product org keys (active-org membership, as `/me` reports them) — the
 * MUTATIONS only. FIX(admin-org-scoped-reads): the read actions moved to family
 * 2 (the reads go to `/api/admin/*` now); the writes still hit the org-scoped
 * product routes, so they are still granted by the ORG list — and only act on
 * the caller's active org (see `useOrgWrite`).
 */
const PRODUCT_ORG_PERM_ACTIONS: Record<string, Action[]> = {
  // Tracker projects (the org-wide work-item project domain) → admin Projects.
  'tracker.manage': [
    'projects:create', 'projects:edit', 'projects:archive', 'projects:manageMembers',
    'tasks:create', 'tasks:reassign', 'tasks:edit', 'tasks:transition', 'tasks:comment', 'tasks:delete',
  ],
  'projects.manage': ['projects:edit', 'projects:archive', 'projects:manageMembers'],
  'projects.assign_manager': ['projects:edit'],
  // Meeting scheduling (server modules/meetings): the organizer/manage writes
  // (create/edit/cancel/rsvp/invite) require `meetings.manage`.
  //
  // FIX(admin-perms-recordings): recording DELETE rides the product route —
  // `DELETE /api/meetings/recordings/:id` is `requirePerm('meetings.manage')`.
  'meetings.manage': ['meetings:create', 'meetings:manage', 'meetings:rerun', 'meetings:delete', 'recordings:delete'],
};

/** Always available to anyone who is staff: the landing page + own settings. */
const STAFF_BASELINE: Action[] = ['dashboard:view', 'settings:view'];

/**
 * Resolve the server's flat `/me` permission list + `platformSurfacePermissions`
 * to the admin Actions the backend will actually honour. See the block comment
 * above for the three gate families.
 *
 * `platformSurfacePerms` is what `requirePlatformPerm` grants this user; it alone
 * decides the admin-surface org keys (family 2). `null`/omitted (an older server
 * that doesn't send it) means none — never a role-name guess.
 */
export function actionsFromServerPerms(
  perms: readonly string[],
  platformSurfacePerms?: readonly string[] | null,
): ReadonlySet<Action> {
  const staff = new Set<Action>();

  for (const p of perms) for (const a of PLATFORM_PERM_ACTIONS[p] ?? []) staff.add(a);
  for (const key of platformSurfacePerms ?? []) {
    for (const a of SURFACE_ORG_PERM_ACTIONS[key] ?? []) staff.add(a);
  }

  // Not platform staff → nothing, however many product/org keys the list holds.
  if (staff.size === 0) return staff;

  for (const a of STAFF_BASELINE) staff.add(a);
  for (const p of perms) for (const a of PRODUCT_ORG_PERM_ACTIONS[p] ?? []) staff.add(a);
  return staff;
}

/** Is `action` in `granted`? */
export function checkAction(granted: ReadonlySet<Action>, action: Action): boolean {
  return granted.has(action);
}

export function isHighRisk(action: Action): boolean {
  return HIGH_RISK_ACTIONS.has(action);
}
